import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";
import {CreatePricingSchemeRequest, PricingScheme, UpdatePricingSchemeRequest} from '@/models/pricing'
import log from '@/lib/logger'

const NBH_PRICING_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/pricing-schemes`

// Helper function to get auth headers
function getAuthHeaders(token: string | null): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        ...(token && {'Authorization': `Bearer ${token}`})
    }
}

export async function getPricingSchemes(token: string | null): Promise<PricingScheme[]> {
    if (!token) {
        log.error("No token provided to request pricing schemes");
        return Promise.reject("No token provided");
    }

    try {
        const response = await fetch(NBH_PRICING_BASE_URL, {
            method: 'GET',
            headers: getAuthHeaders(token)
        });

        // If no pricing schemes exist, the backend might return 404
        if (response.status === 404) {
            return []
        }

        const result = await handleResponse<PricingScheme[]>(response)
        return result || []
    } catch (error: any) {
        // If it's a 404 error, return empty array instead of throwing
        if (error.status === 404) {
            return []
        }
        throw error
    }
}

export async function getPricingScheme(token: string | null, id: string): Promise<PricingScheme> {
    if (!token) {
        log.error("No token provided to request pricing scheme");
        return Promise.reject("No token provided");
    }

    const response = await fetch(`${NBH_PRICING_BASE_URL}/${id}`, {
        method: 'GET',
        headers: getAuthHeaders(token)
    });

    const result = await handleResponse<PricingScheme>(response)
    if (!result) {
        throw new Error("No pricing scheme data received")
    }
    return result
}

export async function createPricingScheme(token: string | null, data: CreatePricingSchemeRequest): Promise<PricingScheme> {
    if (!token) {
        log.error("No token provided to create pricing scheme");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(NBH_PRICING_BASE_URL, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data)
    });

    const result = await handleResponse<PricingScheme>(response)
    if (!result) {
        throw new Error("No pricing scheme data received")
    }
    return result
}

export async function updatePricingScheme(token: string | null, data: UpdatePricingSchemeRequest): Promise<PricingScheme> {
    if (!token) {
        log.error("No token provided to update pricing scheme");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_PRICING_BASE_URL}/${data.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data)
    });

    const result = await handleResponse<PricingScheme>(response)
    if (!result) {
        throw new Error("No pricing scheme data received")
    }
    return result
}

export async function updatePricingSchemeMetadata(token: string | null, id: string, data: {
    name: string,
    description?: string
}): Promise<PricingScheme> {
    if (!token) {
        log.error("No token provided to update pricing scheme metadata");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_PRICING_BASE_URL}/${id}/metadata`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data)
    });

    const result = await handleResponse<PricingScheme>(response)
    if (!result) {
        throw new Error("No pricing scheme data received")
    }
    return result
}

export async function deletePricingScheme(token: string | null, id: string): Promise<void> {
    if (!token) {
        log.error("No token provided to delete pricing scheme");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_PRICING_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
    });

    return handleResponse<void>(response)
}

export async function getActivePricingSchemes(token: string | null): Promise<PricingScheme[]> {
    if (!token) {
        log.error("No token provided to request active pricing schemes");
        return Promise.reject("No token provided");
    }

    const response = await fetch(`${NBH_PRICING_BASE_URL}/active`, {
        method: 'GET',
        headers: getAuthHeaders(token)
    });

    const result = await handleResponse<PricingScheme[]>(response)
    return result || []
}

export async function getCurrentActivePricingScheme(token: string | null): Promise<PricingScheme | null> {
    if (!token) {
        log.error("No token provided to request current active pricing scheme");
        return Promise.reject("No token provided");
    }

    try {
        const response = await fetch(`${NBH_PRICING_BASE_URL}/current`, {
            method: 'GET',
            headers: getAuthHeaders(token)
        });

        if (response.status === 404) {
            return null
        }

        const result = await handleResponse<PricingScheme>(response)
        return result || null
    } catch (error: any) {
        if (error.status === 404) {
            return null
        }
        throw error
    }
}

// Legacy service object for backward compatibility - should be migrated to individual functions
export const pricingService = {
    async getPricingSchemes(): Promise<PricingScheme[]> {
        const token = localStorage.getItem('token')
        return getPricingSchemes(token)
    },

    async getPricingScheme(id: string): Promise<PricingScheme | null> {
        const token = localStorage.getItem('token')
        try {
            return await getPricingScheme(token, id)
        } catch (error: any) {
            if (error.status === 404) {
                return null
            }
            throw error
        }
    },

    async createPricingScheme(
        _organizationId: string,
        data: CreatePricingSchemeRequest
    ): Promise<PricingScheme> {
        const token = localStorage.getItem('token')
        return createPricingScheme(token, data)
    },

    async updatePricingScheme(data: UpdatePricingSchemeRequest): Promise<PricingScheme> {
        const token = localStorage.getItem('token')
        return updatePricingScheme(token, data)
    },

    async updatePricingSchemeMetadata(id: string, data: {
        name: string,
        description?: string
    }): Promise<PricingScheme> {
        const token = localStorage.getItem('token')
        return updatePricingSchemeMetadata(token, id, data)
    },

    async deletePricingScheme(id: string): Promise<void> {
        const token = localStorage.getItem('token')
        return deletePricingScheme(token, id)
    },

    // Hilfsfunktion um den nächsten Monatsanfang zu berechnen
    getNextMonthStart(): string {
        const now = new Date()
        const year = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear()
        const month = now.getMonth() === 11 ? 0 : now.getMonth() + 1
        // Formatiere als YYYY-MM-DD
        const monthStr = (month + 1).toString().padStart(2, '0')
        return `${year}-${monthStr}-01`
    },

    // Prüft ob es Überlappungen mit anderen Schemas gibt
    async checkForOverlappingSchemes(
        _organizationId: string,
        validFrom: string,
        validUntil: string | undefined,
        excludeSchemeId?: string
    ): Promise<boolean> {
        const existingSchemes = await this.getPricingSchemes()
        const newFrom = new Date(validFrom)
        const newUntil = validUntil ? new Date(validUntil) : null

        return existingSchemes.some(scheme => {
            // Skip if it's the same scheme we're updating
            if (excludeSchemeId && scheme.id === excludeSchemeId) {
                return false
            }

            const existingFrom = new Date(scheme.validFrom)
            const existingUntil = scheme.validUntil ? new Date(scheme.validUntil) : null

            // Check for overlaps
            if (newUntil === null && existingUntil === null) {
                // Both are infinite, they overlap
                return true
            } else if (newUntil === null) {
                // New is infinite, check if it starts before existing ends
                return newFrom <= existingUntil!
            } else if (existingUntil === null) {
                // Existing is infinite, check if new ends after existing starts
                return newUntil >= existingFrom
            } else {
                // Both have end dates, check for any overlap
                return !(newUntil < existingFrom || newFrom > existingUntil)
            }
        })
    }
}
